import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabaseClient";

const CATEGORY_LABELS = {
  moderation: "Moderación",
  novedades: "Novedades",
  nuevos_lugares: "Lugares nuevos",
  general: "Aviso de Venaqqi",
};

export const mergeUnreadNotifications = (current = [], incoming = []) => {
  const byId = new Map();
  [...current, ...incoming].forEach(item => {
    if (!item?.id || item.read_at) return;
    byId.set(item.id, item);
  });
  return [...byId.values()].sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
};

export const safeNotificationTarget = value => {
  try {
    const target = new URL(value || "/", window.location.origin);
    if (target.origin !== window.location.origin) return "/";
    return `${target.pathname}${target.search}${target.hash}` || "/";
  } catch {
    return "/";
  }
};

export const notificationTarget = notification => {
  const entityType = String(notification?.data?.entity_type || "").toLowerCase();
  const entityId = notification?.data?.entity_id;
  if (entityType === "review" && entityId !== null && entityId !== undefined && String(entityId).trim()) {
    return `/reviews/${encodeURIComponent(String(entityId))}`;
  }
  return safeNotificationTarget(notification?.deep_link);
};

export default function UnreadNotificationGate() {
  const [userId, setUserId] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const activeUserRef = useRef(null);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setUserId(data?.session?.user?.id || null);
    }).catch(() => {});

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      setUserId(session?.user?.id || null);
    });

    return () => {
      active = false;
      data?.subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    activeUserRef.current = userId;
    if (!userId) {
      setItems([]);
      setError("");
      setBusy(false);
    }
  }, [userId]);

  const loadUnread = useCallback(async () => {
    if (!userId) return;
    const requestedFor = userId;
    const { data, error: queryError } = await supabase
      .from("notifications")
      .select("id,category,title,body,deep_link,data,created_at,read_at")
      .eq("user_id", requestedFor)
      .is("read_at", null)
      .order("created_at", { ascending: true })
      .limit(20);

    if (activeUserRef.current !== requestedFor) return;
    if (queryError) {
      setError("No se pudieron cargar tus avisos pendientes.");
      return;
    }
    setError("");
    setItems(mergeUnreadNotifications([], data || []));
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;
    loadUnread();

    let channel;
    try {
      channel = supabase.channel(`unread-notification-gate:${userId}`)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, payload => {
          if (cancelled || activeUserRef.current !== userId || payload.new?.read_at) return;
          setItems(current => mergeUnreadNotifications(current, [payload.new]));
        })
        .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, payload => {
          if (cancelled || activeUserRef.current !== userId) return;
          if (payload.new?.read_at) setItems(current => current.filter(item => item.id !== payload.new.id));
          else setItems(current => mergeUnreadNotifications(current, [payload.new]));
        })
        .subscribe();
    } catch {
      channel = null;
    }

    return () => {
      cancelled = true;
      if (channel && typeof supabase.removeChannel === "function") supabase.removeChannel(channel);
    };
  }, [loadUnread, userId]);

  const current = items[0] || null;
  const remaining = items.length;
  const label = useMemo(() => current ? (CATEGORY_LABELS[current.category] || "Aviso de Venaqqi") : "Aviso de Venaqqi", [current]);

  const acknowledge = async navigate => {
    if (!current || !userId || busy) return;
    const acknowledgingUser = userId;
    setBusy(true);
    setError("");
    try {
      const now = new Date().toISOString();
      const { error: updateError } = await supabase
        .from("notifications")
        .update({ read_at: now })
        .eq("id", current.id)
        .eq("user_id", acknowledgingUser);
      if (updateError) throw updateError;
      if (activeUserRef.current !== acknowledgingUser) return;

      const target = notificationTarget(current);
      setItems(list => list.filter(item => item.id !== current.id));
      if (navigate) window.location.assign(target);
    } catch {
      if (activeUserRef.current === acknowledgingUser) setError("No pudimos registrar el aviso como visto. Intenta nuevamente.");
    } finally {
      if (activeUserRef.current === acknowledgingUser) setBusy(false);
    }
  };

  if (!current) return null;

  const hasDestination = notificationTarget(current) !== "/";

  return (
    <div role="presentation" style={{ position: "fixed", inset: 0, zIndex: 25000, background: "rgba(0,0,0,.78)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <section role="dialog" aria-modal="true" aria-labelledby="venaqqi-unread-title" style={{ width: "min(100%, 420px)", background: "#111", border: "1px solid #3a2525", borderRadius: 18, boxShadow: "0 24px 70px rgba(0,0,0,.6)", overflow: "hidden" }}>
        <div style={{ height: 4, background: "#e53935" }} />
        <div style={{ padding: "20px 20px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
            <span style={{ color: "#e57373", fontSize: 10, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase" }}>{label}</span>
            {remaining > 1 && <span style={{ color: "#666", fontSize: 10 }}>{remaining} pendientes</span>}
          </div>
          <h2 id="venaqqi-unread-title" style={{ color: "#fff", fontSize: 19, lineHeight: 1.25, margin: "0 0 10px" }}>{current.title || "Mensaje de Venaqqi"}</h2>
          <p style={{ color: "#bbb", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap", margin: 0 }}>{current.body || "Tienes un nuevo aviso."}</p>
          {error && <p role="alert" style={{ margin: "14px 0 0", color: "#ff8a80", background: "#351515", border: "1px solid #5a2525", borderRadius: 9, padding: 9, fontSize: 11 }}>{error}</p>}
          <div style={{ display: "flex", gap: 9, marginTop: 18 }}>
            <button type="button" disabled={busy} onClick={() => acknowledge(false)} style={{ flex: 1, borderRadius: 10, padding: "11px 12px", border: "1px solid #383838", background: "#1a1a1a", color: "#ddd", fontWeight: 800, cursor: busy ? "wait" : "pointer", opacity: busy ? .6 : 1 }}>{remaining > 1 ? "Visto · siguiente" : "Entendido"}</button>
            {hasDestination && <button type="button" disabled={busy} onClick={() => acknowledge(true)} style={{ flex: 1, borderRadius: 10, padding: "11px 12px", border: "1px solid #e53935", background: "#e53935", color: "#fff", fontWeight: 900, cursor: busy ? "wait" : "pointer", opacity: busy ? .6 : 1 }}>Abrir</button>}
          </div>
        </div>
      </section>
    </div>
  );
}
